import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X, Check, Settings2 } from "lucide-react";
import { Link } from "react-router-dom";

const COOKIE_CONSENT_KEY = "vazoul-cookie-consent";

type ConsentLevel = "essential" | "analytics" | "all";

export function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Small delay so it doesn't flash immediately
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const accept = (level: ConsentLevel) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({ level, timestamp: new Date().toISOString() }));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className="fixed bottom-0 left-0 right-0 z-[9998] p-4"
      >
        <div className="max-w-4xl mx-auto rounded-2xl border border-border bg-card/95 backdrop-blur-xl shadow-2xl overflow-hidden">
          <div className="p-4 sm:p-6">
            <div className="flex items-start gap-4">
              <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <Cookie className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-bold text-foreground mb-1">Cookie Preferences</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  We use essential cookies to make Vazoul work. With your consent, we may also use analytics cookies to improve your experience.{" "}
                  <Link to="/legal/cookies" className="text-primary hover:underline">
                    Learn more
                  </Link>
                </p>

                <AnimatePresence>
                  {showDetails && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="mt-3 space-y-2">
                        <div className="flex items-center justify-between p-3 rounded-xl bg-muted/30 border border-border/50">
                          <div>
                            <p className="text-xs font-semibold text-foreground">Essential Cookies</p>
                            <p className="text-[10px] text-muted-foreground">Authentication, security, and core functionality</p>
                          </div>
                          <span className="text-[10px] font-bold text-success bg-success/10 px-2 py-0.5 rounded-full">Always On</span>
                        </div>
                        <div className="flex items-center justify-between p-3 rounded-xl bg-muted/30 border border-border/50">
                          <div>
                            <p className="text-xs font-semibold text-foreground">Analytics Cookies</p>
                            <p className="text-[10px] text-muted-foreground">Usage analytics to improve the platform</p>
                          </div>
                          <span className="text-[10px] font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded-full">Optional</span>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  <button
                    onClick={() => accept("essential")}
                    className="text-xs font-medium px-4 py-2 rounded-xl border border-border text-foreground hover:bg-secondary/50 transition-colors"
                  >
                    Essential Only
                  </button>
                  <button
                    onClick={() => setShowDetails(!showDetails)}
                    className="text-xs font-medium px-4 py-2 rounded-xl border border-border text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors flex items-center gap-1"
                  >
                    <Settings2 className="h-3 w-3" />
                    Customize
                  </button>
                  <button
                    onClick={() => accept("all")}
                    className="text-xs font-bold px-5 py-2 rounded-xl btn-gradient text-primary-foreground flex items-center gap-1"
                  >
                    <Check className="h-3 w-3" />
                    Accept All
                  </button>
                </div>
              </div>
              <button
                onClick={() => accept("essential")}
                className="p-1.5 rounded-lg hover:bg-secondary/50 text-muted-foreground hover:text-foreground transition-colors shrink-0"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
